/**
 * SceneObjectIdentity — stable identity across SceneObject recreation.
 *
 * Undo commands capture a `SceneObject` reference at construction. A bare
 * reference goes dead the moment its owner is disposed by another command's
 * undo — e.g. `BehaviorCommitCommand` captures Cube; user undoes the cube's
 * `CreateObjectCommand`; the cube is destroyed; user redoes the create,
 * which produces a *new* Cube instance at the same path; the behavior
 * commit's redo still targets the destroyed original and silently no-ops.
 *
 * The fix is an indirection: commands store a `SceneObjectRef` whose
 * `current` pointer is rebound by Create/Delete commands during their
 * undo/redo cycles. Every command targeting the same logical identity
 * shares one `SceneObjectRef` and so sees the live instance after rebinding.
 *
 * Identity is established at command-construction time via a
 * `SceneObjectIdentityRegistry` owned by the `TraceScene`. The registry
 * coalesces by instance: `refFor(obj)` returns the same `SceneObjectRef`
 * for repeated calls with the same `SceneObject`. When a Create command
 * rebinds, it also updates the registry so future `refFor(newInstance)`
 * returns the existing shared ref — important when later commands arrive
 * via a code path that only sees the new instance.
 *
 * Public command constructors still accept `SceneObject` references, per
 * the hard rule against string targets. The ref indirection is an internal
 * implementation detail of the undo system.
 */

import type { SceneObject } from './SceneObject';

/**
 * Mutable pointer to the current live `SceneObject` for a single logical
 * identity.
 *
 * Commands hold a `SceneObjectRef` and call `get()` at undo/redo time. A
 * `null` return means the identity is currently unbound (e.g. its owning
 * Create command has been undone, or its enclosing scene was disposed) —
 * the command should silently no-op in that case.
 */
export class SceneObjectRef {
    private _current: SceneObject | null;
    private _lastKnownName: string;

    /** @internal — constructed only via SceneObjectIdentityRegistry. */
    constructor(initial: SceneObject | null) {
        this._current = initial;
        this._lastKnownName = initial?.name ?? '<unbound>';
    }

    /**
     * Returns the current live SceneObject, or `null` if the identity is
     * currently unbound.
     */
    get(): SceneObject | null {
        return this._current;
    }

    /** Last name observed while bound to a non-null instance. Debug aid. */
    get lastKnownName(): string {
        return this._lastKnownName;
    }

    /**
     * Rebind to a new instance (or `null` to mark the identity as
     * temporarily unbound). Called by Create/Delete commands during their
     * undo/redo cycles, via the registry's `rebind()` method — not by
     * other code.
     *
     * @internal
     */
    _bind(obj: SceneObject | null): void {
        this._current = obj;
        if (obj) this._lastKnownName = obj.name;
    }
}

/**
 * Per-scene registry that hands out `SceneObjectRef`s and coalesces them
 * by SceneObject instance. Every `TraceScene` owns one.
 */
export class SceneObjectIdentityRegistry {
    /**
     * Maps each currently-known SceneObject instance to its ref. WeakMap so
     * disposed instances drop out of the map automatically without leaking
     * memory.
     */
    private byInstance: WeakMap<SceneObject, SceneObjectRef> = new WeakMap();

    /**
     * Get the ref tracking the given SceneObject, creating one if none
     * exists yet. The returned ref starts bound to `obj`.
     */
    refFor(obj: SceneObject): SceneObjectRef {
        const existing = this.byInstance.get(obj);
        if (existing) return existing;
        const ref = new SceneObjectRef(obj);
        this.byInstance.set(obj, ref);
        return ref;
    }

    /**
     * Allocate a ref that starts unbound. Used by replay-time deserialization
     * (`fromSerialized`) when the target instance doesn't exist yet — the
     * command's first `redo()` rebinds the ref to the freshly created object.
     */
    allocateUnbound(): SceneObjectRef {
        return new SceneObjectRef(null);
    }

    /**
     * Rebind an existing ref to a new SceneObject instance (or `null` to
     * mark it temporarily unbound). Used by Create/Delete commands during
     * their undo/redo cycles.
     *
     * If `newObj` is non-null, the registry's WeakMap is updated so future
     * `refFor(newObj)` returns the same shared ref.
     */
    rebind(ref: SceneObjectRef, newObj: SceneObject | null): void {
        ref._bind(newObj);
        if (newObj) this.byInstance.set(newObj, ref);
    }
}
